import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  BatchRecordDoughBatch,
  BatchRecordDoughBatchesState,
  CreateBatchRecordDoughBatchRequest
} from '../../types/batchRecordDoughBatch';
import { batchRecordDoughBatchApiService } from '../../services/batchRecordDoughBatchApiService';

// Async thunks

/**
 * Fetch all batch record dough batches from the API
 */
export const fetchAllBatchRecordDoughBatches = createAsyncThunk(
  'batchRecordDoughBatches/fetchAll',
  async () => {
    return await batchRecordDoughBatchApiService.fetchAllBatchRecordDoughBatches();
  }
);

/**
 * Fetch batch record dough batches for a specific batch record
 */
export const fetchBatchRecordDoughBatchesByBatchRecordId = createAsyncThunk(
  'batchRecordDoughBatches/fetchByBatchRecordId',
  async (batchRecordId: string) => {
    return await batchRecordDoughBatchApiService.fetchBatchRecordDoughBatchesByBatchRecordId(batchRecordId);
  }
);

/**
 * Create a new batch record dough batch
 */
export const createBatchRecordDoughBatch = createAsyncThunk(
  'batchRecordDoughBatches/create',
  async (data: CreateBatchRecordDoughBatchRequest) => {
    return await batchRecordDoughBatchApiService.createBatchRecordDoughBatch(data);
  }
);

/**
 * Delete a batch record dough batch
 */
export const deleteBatchRecordDoughBatch = createAsyncThunk(
  'batchRecordDoughBatches/delete',
  async (id: string) => {
    await batchRecordDoughBatchApiService.deleteBatchRecordDoughBatch(id);
    return id;
  }
);

// Initial state
const initialState: BatchRecordDoughBatchesState = {
  items: [],
  loading: false,
  error: null,
};

const batchRecordDoughBatchSlice = createSlice({
  name: 'batchRecordDoughBatches',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch all batch record dough batches
    builder
      .addCase(fetchAllBatchRecordDoughBatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllBatchRecordDoughBatches.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchAllBatchRecordDoughBatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch batch record dough batches';
      });

    // Fetch by batch record ID
    builder
      .addCase(fetchBatchRecordDoughBatchesByBatchRecordId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBatchRecordDoughBatchesByBatchRecordId.fulfilled, (state, action) => {
        state.loading = false;
        // Replace items for this batch record
        const batchRecordId = action.payload[0]?.batchRecordId;
        if (batchRecordId) {
          state.items = [
            ...state.items.filter(i => i.batchRecordId !== batchRecordId),
            ...action.payload
          ];
        }
      })
      .addCase(fetchBatchRecordDoughBatchesByBatchRecordId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch batch record dough batches';
      });

    // Create batch record dough batch
    builder
      .addCase(createBatchRecordDoughBatch.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(createBatchRecordDoughBatch.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create batch record dough batch';
      });

    // Delete batch record dough batch
    builder
      .addCase(deleteBatchRecordDoughBatch.fulfilled, (state, action) => {
        state.items = state.items.filter(i => i.id !== action.payload);
      })
      .addCase(deleteBatchRecordDoughBatch.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete batch record dough batch';
      });
  }
});

export const { clearError } = batchRecordDoughBatchSlice.actions;
export default batchRecordDoughBatchSlice.reducer;
